import React, { useCallback, useState } from 'react';
import { Box, Checkbox, LoadingSpinner, Table } from 'edenred-ui';
import { useTranslation } from 'react-i18next';

import { IBeneficiariesTable } from '@epi-models/pages/Beneficiaries';
import { TableNoContent } from '@epi-pages/Reports/TableNoContent';
import { isActiveBeneficiary } from '@epi-helpers/BeneficiaryHelper';

import { BeneficiariesRow, ChangeGroupActions } from './components';
import { CheckboxCell, NameCell } from './BeneficiariesTable.styled';

export const BeneficiariesTable = ({
  beneficiaries,
  allBeneficiaries,
  showDetail,
  isLoading,
  groupSettings
}: IBeneficiariesTable) => {
  const { t } = useTranslation();
  const [selectedBeneficiaryIds, setSelectedBeneficiaryIds] = useState<
    string[]
  >([]);

  const toggleSelect = useCallback((personId: string) => {
    setSelectedBeneficiaryIds(prev =>
      prev.includes(personId)
        ? prev.filter(id => id !== personId)
        : [...prev, personId]
    );
  }, []);

  const visibleActiveIds = beneficiaries
    .filter(isActiveBeneficiary)
    .map(b => b.personId);

  const allSelected =
    beneficiaries.length > 0 &&
    visibleActiveIds.every(id => selectedBeneficiaryIds.includes(id));
  const someSelected = selectedBeneficiaryIds.length > 0;

  const toggleSelectAll = useCallback(() => {
    setSelectedBeneficiaryIds(prev => {
      const ids = allBeneficiaries
        .filter(isActiveBeneficiary)
        .map(b => b.personId);
      const allSelected = ids.length > 0 && ids.every(id => prev.includes(id));
      return allSelected ? [] : ids;
    });
  }, [allBeneficiaries]);

  return (
    <Box pt={2} pb={6}>
      <ChangeGroupActions
        allBeneficiaries={allBeneficiaries}
        selectedBeneficiaryIds={selectedBeneficiaryIds}
        disableChangeGroup={selectedBeneficiaryIds.length === 0}
        setSelectedBeneficiaryIds={setSelectedBeneficiaryIds}
      />
      <Table id="manage-employees-table">
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <Table.Head>
              <Table.Row>
                <CheckboxCell
                  id="manage-employees-table-employee-checkbox"
                  width="24px"
                >
                  <Checkbox
                    noPadding
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={toggleSelectAll}
                  />
                </CheckboxCell>
                <NameCell id="manage-employees-table-employee-title">
                  {t('containers.beneficiaries.table.employee')}
                </NameCell>
                <Table.Cell id="manage-employees-table-email-title">
                  {t('containers.beneficiaries.table.email')}
                </Table.Cell>
                <Table.Cell id="manage-employees-table-email-title">
                  {t('containers.beneficiaries.table.employment_type')}
                </Table.Cell>
                <Table.Cell id="manage-employees-table-email-title">
                  {t('containers.beneficiaries.table.benefit_group')}
                </Table.Cell>
                <Table.Cell
                  id="manage-employees-table-actions-title"
                  width="240px"
                ></Table.Cell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {beneficiaries.length > 0 ? (
                beneficiaries.map(beneficiary => (
                  <BeneficiariesRow
                    key={beneficiary.personId}
                    beneficiary={beneficiary}
                    showDetail={showDetail}
                    groupSetting={groupSettings?.find(
                      s => s.groupId === beneficiary.groupId
                    )}
                    isSelected={selectedBeneficiaryIds.includes(
                      beneficiary.personId
                    )}
                    onToggleSelect={toggleSelect}
                  />
                ))
              ) : (
                <TableNoContent
                  colSpan={6}
                  text={t('containers.beneficiaries.table.no_data')}
                />
              )}
            </Table.Body>
          </>
        )}
      </Table>
    </Box>
  );
};
